import * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import { cn } from 'src/lib/utils';
import { buttonVariants } from 'src/components/ui/button';

export const ConfirmDialog = AlertDialogPrimitive.Root;

export const ConfirmDialogTrigger = AlertDialogPrimitive.Trigger;

export const ConfirmDialogPortal = AlertDialogPrimitive.Portal;

export const ConfirmDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      'tw-fixed tw-inset-0 tw-z-[1050] tw-bg-black/70 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0',
      className,
    )}
    {...props}
    ref={ref}
  />
));
ConfirmDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

export const ConfirmDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => (
  <ConfirmDialogPortal>
    <ConfirmDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        'tw-fixed tw-left-[50%] tw-top-[50%] tw-z-[1050] tw-flex tw-w-full tw-max-w-lg tw-translate-x-[-50%] tw-translate-y-[-50%] tw-flex-col tw-gap-6 tw-border tw-bg-card tw-p-6 tw-shadow-lg tw-duration-200 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[state=closed]:tw-slide-out-to-left-1/2 data-[state=closed]:tw-slide-out-to-top-[48%] data-[state=open]:tw-slide-in-from-left-1/2 data-[state=open]:tw-slide-in-from-top-[48%] sm:tw-rounded-lg',
        className,
      )}
      {...props}
    />
  </ConfirmDialogPortal>
));
ConfirmDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

export const ConfirmDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('tw-flex tw-flex-col tw-space-y-2 tw-text-center sm:tw-text-left', className)}
    {...props}
  />
);
ConfirmDialogHeader.displayName = 'ConfirmDialogHeader';

export const ConfirmDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'tw-flex tw-flex-col-reverse sm:tw-flex-row sm:tw-justify-end sm:tw-space-x-2',
      className,
    )}
    {...props}
  />
);
ConfirmDialogFooter.displayName = 'AlertDialogFooter';

export const ConfirmDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn('tw-text-2xl tw-font-bold tw-text-dark', className)}
    {...props}
  />
));
ConfirmDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

export const ConfirmDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn('tw-min-h-10 tw-text-muted-foreground', className)}
    {...props}
  />
));
ConfirmDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

export const ConfirmDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(buttonVariants({ variant: 'brand' }), className)}
    {...props}
  />
));
ConfirmDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

export const ConfirmDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(
      buttonVariants({ variant: 'outline' }),
      'tw-border-transparent tw-text-brand hover:tw-border-accent hover:tw-bg-purple-200 hover:tw-text-brand',
      className,
    )}
    {...props}
  />
));
ConfirmDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;
